'use client'

import { ReactNode } from 'react'
import { useOurStore } from '@/app/_providers/StoreProvider'
import s from './DebugPanel.module.css'
import { useCart } from '@/app/_providers/CartProvider/CartProvider'

const format = (n: number): string => `$${n.toFixed(2)}`

const CartDebug = (): ReactNode => {
  const { cartId, localLineItems, lineItemsToDelete } = useOurStore((state) => state)
  const { cart, loading: cartLoading, error: cartError } = useCart()

  const hasLocalItems = localLineItems.length > 0
  const hasDeleteItems = lineItemsToDelete.length > 0
  const hasCartItems = cart.lineItems.length > 0

  return (
    <details className={s.panel} aria-label="Cart debug panel">
      <summary className={s.summary}>
        <span className={s.summaryIcon} aria-hidden="true">&#9654;</span>
        <span className={s.summaryTitle}>Cart Debug</span>
        {cartError
          ? <span className={`${s.summaryBadge} ${s.summaryBadgeError}`}>Error</span>
          : <span className={`${s.summaryBadge} ${s.summaryBadgeOk}`}>{cart.state}</span>}
      </summary>

      <div className={s.body}>
        {/* ── Identity & Status ─────────────────────────── */}
        <details className={s.section} open>
          <summary className={s.sectionSummary}>
            <span className={s.sectionIcon} aria-hidden="true">&#9654;</span>
            <span className={s.sectionTitle}>Status</span>
          </summary>
          <div className={s.sectionBody}>
            <div className={s.row}>
              <span className={s.key}>cartId</span>
              <span className={cartId ? s.valString : s.valNull}>
                {cartId ?? 'null'}
              </span>
            </div>
            <div className={s.row}>
              <span className={s.key}>loading</span>
              <span className={s.valNumber}>{String(cartLoading)}</span>
            </div>
            <div className={s.row}>
              <span className={s.key}>state</span>
              <span className={s.val}>
                <span className={`${s.pill} ${cart.state === 'NEW' ? s.pillMuted : s.pillOk}`}>
                  {cart.state}
                </span>
              </span>
            </div>
            {cartError && (
              <div className={s.row}>
                <span className={s.key}>error</span>
                <span className={s.valError}>{cartError.message}</span>
              </div>
            )}
          </div>
        </details>

        {/* ── Totals ────────────────────────────────────── */}
        <details className={s.section} open>
          <summary className={s.sectionSummary}>
            <span className={s.sectionIcon} aria-hidden="true">&#9654;</span>
            <span className={s.sectionTitle}>Totals</span>
          </summary>
          <div className={s.sectionBody}>
            <div className={s.row}>
              <span className={s.key}>tickets</span>
              <span className={s.valNumber}>{cart.ticketCount}</span>
            </div>
            <div className={s.row}>
              <span className={s.key}>subtotal</span>
              <span className={s.valNumber}>{format(cart.subTotal)}</span>
            </div>
            <div className={s.row}>
              <span className={s.key}>fees</span>
              <span className={s.valNumber}>{format(cart.feesTotal)}</span>
            </div>
            <div className={s.row}>
              <span className={s.key}>tax</span>
              <span className={s.valNumber}>{format(cart.taxesTotal)}</span>
            </div>
            <div className={s.row}>
              <span className={s.key}>grand total</span>
              <span className={s.valNumber}>{format(cart.grandTotal)}</span>
            </div>
            <div className={s.row}>
              <span className={s.key}>expires</span>
              <span className={cart.expiresAt ? s.valString : s.valNull}>
                {cart.expiresAt ?? 'null'}
              </span>
            </div>
          </div>
        </details>

        {/* ── Remote Purchaser ──────────────────────────── */}
        <details className={s.section}>
          <summary className={s.sectionSummary}>
            <span className={s.sectionIcon} aria-hidden="true">&#9654;</span>
            <span className={s.sectionTitle}>Purchaser (Remote)</span>
          </summary>
          <div className={s.sectionBody}>
            <div className={s.row}>
              <span className={s.key}>id</span>
              <span className={cart.purchaser.id ? s.valString : s.valNull}>
                {cart.purchaser.id || 'null'}
              </span>
            </div>
            <div className={s.row}>
              <span className={s.key}>identityId</span>
              <span className={cart.purchaser.identityId ? s.valString : s.valNull}>
                {cart.purchaser.identityId || 'null'}
              </span>
            </div>
            <div className={s.row}>
              <span className={s.key}>email</span>
              <span className={cart.purchaser.email ? s.valString : s.valNull}>
                {cart.purchaser.email || 'null'}
              </span>
            </div>
            <div className={s.row}>
              <span className={s.key}>name</span>
              <span className={cart.purchaser.firstName ? s.valString : s.valNull}>
                {cart.purchaser.firstName && cart.purchaser.lastName
                  ? `${cart.purchaser.firstName} ${cart.purchaser.lastName}`
                  : 'null'}
              </span>
            </div>
            <div className={s.row}>
              <span className={s.key}>phone</span>
              <span className={cart.purchaser.phoneNumber ? s.valString : s.valNull}>
                {cart.purchaser.phoneNumber || 'null'}
              </span>
            </div>
            <div className={s.row}>
              <span className={s.key}>zipCode</span>
              <span className={cart.purchaser.zipCode ? s.valString : s.valNull}>
                {cart.purchaser.zipCode || 'null'}
              </span>
            </div>
            <div className={s.row}>
              <span className={s.key}>country</span>
              <span className={cart.purchaser.country ? s.valString : s.valNull}>
                {cart.purchaser.country ?? 'null'}
              </span>
            </div>
          </div>
        </details>

        {/* ── Local Line Items ──────────────────────────── */}
        <details className={s.section}>
          <summary className={s.sectionSummary}>
            <span className={s.sectionIcon} aria-hidden="true">&#9654;</span>
            <span className={s.sectionTitle}>Local Line Items</span>
            <span className={s.sectionCount}>
              {`(${localLineItems.length})`}
            </span>
          </summary>
          <div className={s.sectionBody}>
            {!hasLocalItems
              ? <p className={s.empty}>No local line items</p>
              : (
                <table className={s.table}>
                  <caption className={s.srOnly}>Local line items</caption>
                  <thead>
                    <tr>
                      <th scope="col">Tier</th>
                      <th scope="col">Category</th>
                      <th scope="col" className={s.numCell}>Qty</th>
                      <th scope="col" className={s.numCell}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {localLineItems.map((li) => (
                      <tr key={`local-${li.tier.title}-${li.category.title}`}>
                        <td>{li.tier.title}</td>
                        <td>{li.category.title}</td>
                        <td className={s.numCell}>{li.quantity}</td>
                        <td className={s.numCell}>{format(li.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        </details>

        {/* ── Items To Delete ───────────────────────────── */}
        <details className={s.section}>
          <summary className={s.sectionSummary}>
            <span className={s.sectionIcon} aria-hidden="true">&#9654;</span>
            <span className={s.sectionTitle}>To Delete</span>
            <span className={s.sectionCount}>
              {`(${lineItemsToDelete.length})`}
            </span>
          </summary>
          <div className={s.sectionBody}>
            {!hasDeleteItems
              ? <p className={s.empty}>No items pending deletion</p>
              : (
                <table className={s.table}>
                  <caption className={s.srOnly}>Line items pending deletion</caption>
                  <thead>
                    <tr>
                      <th scope="col">Tier</th>
                      <th scope="col">Category</th>
                      <th scope="col" className={s.numCell}>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItemsToDelete.map((li) => (
                      <tr key={`del-${li.tier.title}-${li.category.title}`}>
                        <td>{li.tier.title}</td>
                        <td>{li.category.title}</td>
                        <td className={s.numCell}>{li.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        </details>

        {/* ── Cart Line Items ───────────────────────────── */}
        <details className={s.section}>
          <summary className={s.sectionSummary}>
            <span className={s.sectionIcon} aria-hidden="true">&#9654;</span>
            <span className={s.sectionTitle}>Cart Line Items</span>
            <span className={s.sectionCount}>
              {`(${cart.lineItems.length})`}
            </span>
          </summary>
          <div className={s.sectionBody}>
            {!hasCartItems
              ? <p className={s.empty}>No cart line items</p>
              : (
                <table className={s.table}>
                  <caption className={s.srOnly}>Cart line items from server</caption>
                  <thead>
                    <tr>
                      <th scope="col">Tier</th>
                      <th scope="col">Category</th>
                      <th scope="col" className={s.numCell}>Qty</th>
                      <th scope="col" className={s.numCell}>Amount</th>
                      <th scope="col" className={s.numCell}>Tickets</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cart.lineItems.map((li) => (
                      <tr key={`cart-${li.tier.title}-${li.category.title}`}>
                        <td>{li.tier.title}</td>
                        <td>{li.category.title}</td>
                        <td className={s.numCell}>{li.quantity}</td>
                        <td className={s.numCell}>{format(li.amount)}</td>
                        <td className={s.numCell}>
                          {`${li.tickets.filter((t) => t.inCart).length}/${li.tickets.length}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        </details>
      </div>
    </details>
  )
}

export default CartDebug
